import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2, FileDown, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

import { CHAPTERS } from "@/tutorial/chaptersData";
import { exportTutorialPDF } from "@/tutorial/utils/exportTutorialPDF";
import { exportCertificate } from "@/tutorial/utils/exportCertificate";

import Chapter0 from "@/tutorial/chapters/Chapter0";
import Chapter1 from "@/tutorial/chapters/Chapter1";
import Chapter2 from "@/tutorial/chapters/Chapter2";
import Chapter3 from "@/tutorial/chapters/Chapter3";
import Chapter4 from "@/tutorial/chapters/Chapter4";
import Chapter5 from "@/tutorial/chapters/Chapter5";
import Chapter6 from "@/tutorial/chapters/Chapter6";
import Chapter7 from "@/tutorial/chapters/Chapter7";
import Chapter8 from "@/tutorial/chapters/Chapter8";
import Chapter9 from "@/tutorial/chapters/Chapter9";
import Chapter10 from "@/tutorial/chapters/Chapter10";

const CHAPTER_COMPONENTS = {
  0: Chapter0,
  1: Chapter1,
  2: Chapter2,
  3: Chapter3,
  4: Chapter4,
  5: Chapter5,
  6: Chapter6,
  7: Chapter7,
  8: Chapter8,
  9: Chapter9,
  10: Chapter10,
};

const PdfCtx = createContext(null);

export function useTutorialPDF() {
  const ctx = useContext(PdfCtx);
  if (!ctx) {
    throw new Error("useTutorialPDF must be used inside <TutorialPDFExporter>");
  }
  return ctx;
}

export function TutorialPDFExporter({ children }) {
  const { user } = useAuth();
  const slotRef = useRef(null);
  const setChapterResolverRef = useRef(null);

  const [slotChapterId, setSlotChapterId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, label: "" });

  // After React paints the new chapter into the slot, resolve the pending promise.
  useEffect(() => {
    if (setChapterResolverRef.current) {
      const resolve = setChapterResolverRef.current;
      setChapterResolverRef.current = null;
      // Yield twice to let layout settle
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    }
  }, [slotChapterId]);

  const setSlotChapter = useCallback((id) => {
    return new Promise((resolve) => {
      setChapterResolverRef.current = resolve;
      setSlotChapterId(id);
    });
  }, []);

  const exportPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setProgress({ percent: 0, label: "Préparation…" });
    try {
      await exportTutorialPDF({
        slotEl: slotRef.current,
        setSlotChapter,
        onProgress: setProgress,
      });
      toast.success("Tutoriel téléchargé. Vérifiez vos téléchargements.");
    } catch (e) {
      console.error("PDF export failed", e);
      toast.error(
        "Échec de la génération du PDF : " + (e?.message || "erreur inconnue")
      );
    } finally {
      setExporting(false);
      setSlotChapterId(null);
    }
  }, [exporting, setSlotChapter]);

  const exportCert = useCallback(() => {
    const name = (user?.name || user?.email || "Utilisateur VITAE-PUBLICA").trim();
    try {
      exportCertificate({ userName: name });
      toast.success("Certificat téléchargé.");
    } catch (e) {
      console.error("Certificate export failed", e);
      toast.error("Impossible de générer le certificat.");
    }
  }, [user]);

  const value = useMemo(
    () => ({ exportPDF, exportCert, exporting, progress }),
    [exportPDF, exportCert, exporting, progress]
  );

  const SlotChapter =
    slotChapterId !== null ? CHAPTER_COMPONENTS[String(slotChapterId)] : null;

  return (
    <PdfCtx.Provider value={value}>
      {children}

      {/* ── Off-screen render slot ───────────────────────────────────── */}
      <div
        aria-hidden="true"
        data-pdf-export-slot
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          width: 794,
          background: "#ffffff",
          color: "#0f172a",
          pointerEvents: "none",
          zIndex: -1,
          padding: "32px",
          fontSize: "14px",
        }}
        ref={slotRef}
      >
        {SlotChapter ? <SlotChapter /> : null}
      </div>

      {/* ── Progress modal ───────────────────────────────────────────── */}
      <Dialog open={exporting}>
        <DialogContent
          className="sm:max-w-md"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="tutorial-pdf-export-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-4 w-4 text-brand-gold" />
              Génération du tutoriel PDF en cours
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <div className="text-sm text-muted-foreground">
              {progress.label || "Initialisation…"}
            </div>
            <div
              className="h-3 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress.percent}
              data-testid="tutorial-pdf-export-progress"
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand-blue to-brand-gold transition-all"
                style={{ width: `${progress.percent || 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Capture des {CHAPTERS.length} chapitres…
              </span>
              <span className="font-mono font-semibold text-foreground">
                {progress.percent || 0}%
              </span>
            </div>
            <p className="text-[11px] italic text-muted-foreground">
              La fenêtre se fermera automatiquement à la fin. Ne quittez pas la page.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </PdfCtx.Provider>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Reusable button
 * ──────────────────────────────────────────────────────────────────── */
export function TutorialPDFExportButton({
  variant = "default",
  className = "",
  testId = "tutorial-pdf-export-btn",
  label = "Télécharger le tutoriel complet (PDF)",
}) {
  const { exportPDF, exporting } = useTutorialPDF();
  return (
    <Button
      type="button"
      onClick={exportPDF}
      disabled={exporting}
      data-testid={testId}
      className={
        variant === "primary"
          ? `bg-brand-gold text-background hover:bg-brand-gold/90 ${className}`
          : `border border-brand-gold/60 bg-card text-brand-gold hover:bg-brand-gold/10 ${className}`
      }
      variant="outline"
    >
      {exporting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Génération…
        </>
      ) : (
        <>
          <FileDown className="mr-2 h-4 w-4" />
          {label}
        </>
      )}
    </Button>
  );
}

export function TutorialCertificateButton({ className = "" }) {
  const { exportCert } = useTutorialPDF();
  return (
    <Button
      type="button"
      onClick={exportCert}
      data-testid="tutorial-certificate-btn"
      className={`bg-brand-gold text-background hover:bg-brand-gold/90 ${className}`}
    >
      <FileDown className="mr-2 h-4 w-4" />
      Télécharger mon certificat
    </Button>
  );
}
